import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import {AppConstants, LMConditionUtilService} from '@utils/index';
import {LMListComponent, LMListTransactionEvent} from './lm-list.component';
import {forkJoin, Observable, Observer} from 'rxjs';
import {LMDetailSectionBase} from './lm-detail-section-base';
import {LandmarkPanel, LMFormComponent} from '@form/index'
import {LMContextService, LMLoggerService, LMUrlInfoBuilderService} from '@services/index';
import {LMRefreshRequest} from '@app/components';

interface MultiListSectionDefinition {
  spec: LMListSpec | LMFormSpec,
  visibility: LMCondition,
  hidden: boolean,
  url: LMListUrlInfo | LMFormUrlInfo,
  type: 'list' | 'form'
}

@Component({
  selector: 'lm-detail-section-multilist',
  templateUrl: 'lm-detail-section-multilist.component.html',
})

export class LMDetailSectionMultiListComponent implements LMDetailSectionBase, AfterViewInit, AfterViewChecked, OnDestroy, OnInit {
  @HostBinding('attr.id') get id() {
    return this.testVersion1 ? this.detailSectionId : null;
  };

  @ViewChildren('LMListComponent') listComponents: QueryList<LMListComponent>;
  @ViewChildren('LMFormComponent') formComponents: QueryList<LMFormComponent>;

  // --------------------------------------------
  // INPUTS
  // --------------------------------------------
  @Input()
  set panel(panel: LandmarkPanel) {
    this._panel = panel;

    /* build the id once the panel comes in */
    this.buildDetailSectionId(this.panel);

    this._updateVisibility = !!this.fromDataView;
  }

  get panel(): LandmarkPanel {
    return this._panel;
  }

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;

    this._updateVisibility = !!this.panel;
  }

  @Input() needsReload: boolean;

  @Input() set activated(activated: boolean) {
    this._activated = activated;
  }

  // --------------------------------------------
  // OUTPUTS
  // --------------------------------------------
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() detailSectionDisplayed = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() panelDataUpdated = new EventEmitter<any>();

  // --------------------------------------------
  // VARIABLES
  // --------------------------------------------
  public  sectionDefinitions: Array<MultiListSectionDefinition> = [];
  private _activated = false;
  private _fromDataView: LMDataView = null;
  private _panel: LandmarkPanel = null;
  private detailSectionId: string = null;
  private listDetail: any;
  private _updateVisibility = false;
  readonly instanceId: string;
  public testVersion1: boolean;

  public formOptions: LMFormOptions = {
    isReadOnlyForm: true,
    isMultiSelectContent: true
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private lmConditionUtilService: LMConditionUtilService,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
  }

  ngOnInit() {
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);

    this.panel.sections.forEach((multiList: LMMultiListSectionSpec) => {
      if (multiList.hasOwnProperty('listSpec')) {
        this.sectionDefinitions.push({
          url: this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(multiList.listSpec),
          spec: multiList.listSpec,
          visibility: multiList.visibility,
          hidden: false,
          type: 'list'
        });
      } else if (multiList.hasOwnProperty('formSpec')) {
        this.sectionDefinitions.push({
          spec: multiList.formSpec,
          url: this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(multiList.formSpec),
          visibility: multiList.visibility,
          hidden: false,
          type: 'form'
        })
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this._updateVisibility) {
      this._updateVisibility = false;
      this.updateSectionVisibility();
    }
  }

  ngOnDestroy() {
    if (this.listDetail) {
      this.listDetail.destroy();
    }
  }

  // --------------------------------------------
  // GETTERS
  // --------------------------------------------
  get listSpec(): LMListSpec {
    return this.panel.listSpec;
  }

  get activated() {
    return this._activated;
  }

  get fromDataView(): LMDataView {
    if (this.needsReload) {
      return null;
    }
    return this._fromDataView;
  }

  get type() {
    return 'list';
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onListTransactionEvent(event: LMListTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.contentModified.emit(event);
  }

  onUpdateToolbarStateEvent(event: any) {
    this.updateToolbarStateEvent.emit(event);
  }

  onSectionDisplayed(event: any) {
    this.detailSectionDisplayed.emit(event);
  }

  // --------------------------------------------
  // INFRASTRUCTURE
  // --------------------------------------------
  isModified(): boolean {
    // don't need to check for modified forms, they will always be read only
    if (!this.listComponents) {
      return false;
    }

    return this.listComponents.filter((list) => list.isModified()).length > 0;
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const refreshCalls: Observable<boolean>[] = this.listComponents.toArray().filter(list => list.listId !== request?.originatingSourceId)
        .map((list: LMListComponent) => list.refresh());

      // console.log('multilist to be refresh:' + refreshCalls.length);

      forkJoin(refreshCalls).subscribe(value => {
        observer.next(true);
        observer.complete();
      });
    });
  }

  save(callBack: Observer<boolean>) {
    // forms are read only, so don't need to save any changes
    this.listComponents.forEach((list) => list.runSaveAction(AppConstants.STANDARD_SAVE, callBack));
  }

  saveDetailSectionState() {
    // forms are read only, so don't need to save any changes
    if (this.listComponents) {
      this.listComponents.forEach((list) => list.saveComponentState());
    }
  }

  /**
   * no longer used for testVersion V2 and above.
   */
  private buildDetailSectionId(panel: LandmarkPanel) {
    this.detailSectionId = panel.id + '_panel';
  }

  clearDetailSectionState() {
    if (this.listComponents) {
      this.listComponents.forEach((list: LMListComponent) => list.clearComponentState());
    }
    if (this.formComponents) {
      this. formComponents.forEach((form: LMFormComponent) => form.clearComponentState());
    }
  }

  private updateSectionVisibility() {
    this.sectionDefinitions.forEach((section: MultiListSectionDefinition) => {
      section.hidden = (section.visibility && this.fromDataView) ? !this.lmConditionUtilService.isValid([section.visibility], this.fromDataView) : false;
    })
  }
}
